import { type FC, type FormEvent, type PropsWithChildren } from 'react';
import { useFormContext } from 'react-hook-form';
import { _isNotEmpty, _notNil } from '@/littledash';
import { BiSend } from 'react-icons/bi';
import Button from '@/components/UI/Button/Button';
import { useCanSubmit, useStepCounts } from '../VerticalStepFormContext';
import type { StepConfig } from '../VerticalStepForm.model';
import styles from './FormShell.module.scss';

interface FormShellProps {
  title?: string;
  description?: string;
  steps: Array<StepConfig>;
  submitDescription?: string;
  isSubmitting: boolean;
  onSubmit: (e: FormEvent) => void;
}

/**
 * Outer shell of the step form. Renders the title, description, step list,
 * and a submit step card with progress counts and submit button.
 */
const FormShell: FC<PropsWithChildren<FormShellProps>> = ({
  children,
  title,
  description,
  steps,
  submitDescription,
  isSubmitting,
  onSubmit,
}) => {
  const canSubmit = useCanSubmit();
  const { completed: completedCount, skipped: skippedCount } = useStepCounts();
  const {
    formState: { errors },
  } = useFormContext();
  const hasErrors = _isNotEmpty(Object.keys(errors));
  const isReady = canSubmit && !hasErrors;
  const totalSteps = steps.length;
  const errorCount = Object.keys(errors).length;

  return (
    <form
      onSubmit={onSubmit}
      className={styles.form}
      data-test-component="FormShell"
      data-test-element="form"
      data-testid="step-form-shell"
    >
      {(_notNil(title) || _notNil(description)) && (
        <div className={styles.header}>
          {_notNil(title) && <h2 className={styles.title}>{title}</h2>}
          {_notNil(description) && <p className={styles.description}>{description}</p>}
        </div>
      )}

      <div className={styles.steps}>{children}</div>

      <div className={styles.submitStep}>
        <div className={styles.submitIndicatorColumn}>
          <div
            className={`${styles.submitIndicator} ${isSubmitting ? styles.submitIndicatorSubmitting : isReady ? styles.submitIndicatorReady : ''}`}
          >
            <BiSend size={16} />
          </div>
        </div>
        <div
          className={`${styles.submitCard} ${isSubmitting ? styles.submitCardSubmitting : isReady ? styles.submitCardReady : ''}`}
        >
          <div className={styles.submitContent}>
            <div className={styles.submitInfo}>
              <span className={styles.submitStatus}>
                {totalSteps} steps
                {completedCount > 0 && <span className={styles.statusCompleted}> · {completedCount} completed</span>}
                {skippedCount > 0 && <span className={styles.statusSkipped}> · {skippedCount} skipped</span>}
                {errorCount > 0 && (
                  <span className={styles.statusError}>
                    {' '}
                    · {errorCount} {errorCount === 1 ? 'error' : 'errors'}
                  </span>
                )}
              </span>
              {_notNil(submitDescription) && <span className={styles.submitDescription}>{submitDescription}</span>}
            </div>
            <Button
              submit
              primary
              disabled={!isReady}
              loading={isSubmitting}
              loadingText="Submitting"
              testId="step-form-shell__submit"
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default FormShell;
